/**
 * The CRF Builder keeps its working copy of a study in two places: the browser
 * (localStorage) and Firestore (crfBuilder/session). These two helpers decide
 * what is written and which copy a page load believes.
 *
 * Pure so they can be tested under functions' vitest (`npm run test:web`).
 */

/** The shape both copies share, as far as freshness is concerned. */
export interface TaggedSessionCopy<T> {
  session: T;
  /** Milliseconds, or null when the copy carries no readable time. */
  updatedAt: number | null;
}

/**
 * Recursively drop keys whose value is undefined. Firestore rejects
 * undefined fields and the SDK's validation throws synchronously —
 * which bypasses any .catch() attached to setDoc's promise. So we must
 * strip before the call, not hope to handle errors after.
 */
export function stripUndefined<T>(value: T): T {
  if (value === null || typeof value !== 'object') return value;
  // A Date has no own enumerable keys, so rebuilding it field by field yields
  // {} - which is what the session's updatedAt became, and why every page load
  // preferred the browser copy and wiped the confirmations.
  if (value instanceof Date) return value;
  if (Array.isArray(value)) {
    return value.map(stripUndefined) as unknown as T;
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (v === undefined) continue;
    out[k] = stripUndefined(v);
  }
  return out as T;
}

/**
 * Pick the freshest of (localStorage cache, Firestore session). Firestore
 * wins on ties so cross-device dismissals propagate. Local wins only when
 * its updatedAt is strictly newer than Firestore's — the typical case
 * being a same-tab edit that hasn't yet round-tripped to the server.
 *
 * A copy with NO readable time scores 0. So while the stored copy carries no
 * time - every document written before 2026-09-12, when updatedAt was flattened
 * to {} - the browser copy still wins, exactly as it did then: the first load
 * after the fix can overwrite nothing it would not have overwritten anyway.
 * The page stamps a real time on its next save, and from then on the newer of
 * the two genuinely wins.
 */
export function pickFreshestSession<T>(
  local: TaggedSessionCopy<T> | null,
  server: TaggedSessionCopy<T> | null,
): T | null {
  if (!local && !server) return null;
  if (!server) return local?.session ?? null;
  if (!local) return server.session;
  const lt = local.updatedAt ?? 0;
  const st = server.updatedAt ?? 0;
  return lt > st ? local.session : server.session;
}

/** The fields the merge reads. The page's CRFForm carries these and more. */
export interface MergeableForm {
  id: string;
  status?: string;
  serverUpdatedAt?: string;
  contentFromWorkingCopy?: boolean;
  variables?: unknown[] | null;
  sections?: unknown;
  licensedInstrument?: unknown;
  dismissedWarnings?: unknown[];
  rationale?: string;
}

/**
 * Combine the stored forms (listCrfForms) with the working copy a load picked.
 *
 * Name, status, code and version always come from the stored form. The working
 * copy supplies what the stored form does not keep (licensed flag, dismissed
 * warnings, rationale). A Final form's questions and sections always come from
 * the stored form.
 *
 * A Draft form's questions and sections come from the working copy only while
 * that copy is CURRENT: either the server built the stored questions from it
 * (contentFromWorkingCopy - the Builder's own draft of a form with no uploaded
 * source, or a form never stored), or the copy was saved against the very
 * version being loaded. Otherwise the stored form wins.
 *
 * Until 2026-09-15 a Draft's working copy won whenever it had any questions.
 * On FOG a dictionary adoption rewrote three Draft forms at 15:14; the page
 * opened at 15:16 showed their OLD questions (Non-Motor Rating Scale: 122 of
 * the 189 stored) under the NEW version, so its next edit or Approve would have
 * passed the stale-save check and put the old questions back.
 */
export function mergeServerWithCache<T extends MergeableForm>(serverForms: T[], cachedForms: T[]): T[] {
  const cachedById = new Map(cachedForms.map((f) => [f.id, f]));
  return serverForms.map((server) => {
    const cached = cachedById.get(server.id);
    if (!cached) return server;
    const isFinalOnServer = server.status === 'approved' || server.status === 'final';
    const cachedHasQuestions = Array.isArray(cached.variables) && cached.variables.length > 0;
    const cachedIsCurrent =
      server.contentFromWorkingCopy === true ||
      (typeof cached.serverUpdatedAt === 'string' &&
        cached.serverUpdatedAt !== '' &&
        cached.serverUpdatedAt === server.serverUpdatedAt);
    const useCached = !isFinalOnServer && cachedHasQuestions && cachedIsCurrent;
    return {
      ...server,
      ...(cached.licensedInstrument ? { licensedInstrument: cached.licensedInstrument } : {}),
      ...(cached.dismissedWarnings?.length ? { dismissedWarnings: cached.dismissedWarnings } : {}),
      ...(cached.rationale ? { rationale: cached.rationale } : {}),
      variables: useCached ? cached.variables : server.variables,
      sections: useCached ? cached.sections : server.sections,
    } as T;
  });
}
